import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Line } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Legend } from "chart.js";
import { DateRangePicker } from 'react-date-range';
import { tr } from 'date-fns/locale';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';

import {
  getDailySessionStats,
  getRetentionStats,
  getWeeklyStats
} from '../services/AnalyticsService';

import {
  getGA4AnalyticsDashboard,
  formatGA4DataForDashboard
} from '../services/GoogleAnalyticsService';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Legend);

function SessionStats() {
  const { user } = useSelector((state) => state.auth);
  const [loading, setLoading] = useState(false);
  const [selectionRange, setSelectionRange] = useState({
    startDate: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000), // 7 days ago
    endDate: new Date(),
    key: 'selection',
  });

  const ranges = [selectionRange];

  // State for session analytics data
  const [dailySessionStats, setDailySessionStats] = useState(null);
  const [retentionStats, setRetentionStats] = useState(null);
  const [weeklyStats, setWeeklyStats] = useState(null);
  const [ga4Data, setGA4Data] = useState(null);
  
  // State for previous week comparison data
  const [previousWeekRetentionStats, setPreviousWeekRetentionStats] = useState(null);
  const [previousWeekGA4Data, setPreviousWeekGA4Data] = useState(null);
  
  // State for abandonment rate calculation
  const [abandonmentRate, setAbandonmentRate] = useState(null);
  const [previousWeekAbandonmentRate, setPreviousWeekAbandonmentRate] = useState(null);
  
  // State for platform data
  const [platformData, setPlatformData] = useState(null);
  const [previousWeekPlatformData, setPreviousWeekPlatformData] = useState(null);

  const fetchSessionAnalytics = async () => {
    setLoading(true);
    try {
      const startDate = new Date(selectionRange.startDate);
      const endDate = new Date(selectionRange.endDate);
      endDate.setHours(23, 59, 59, 999);

      // Calculate previous week dates
      const dateRange = endDate.getTime() - startDate.getTime();
      const previousWeekEndDate = new Date(startDate.getTime() - 1);
      const previousWeekStartDate = new Date(previousWeekEndDate.getTime() - dateRange);

      // Format dates for GA4 API
      const ga4StartDate = startDate.toISOString().split('T')[0];
      const ga4EndDate = endDate.toISOString().split('T')[0];
      const ga4PreviousStartDate = previousWeekStartDate.toISOString().split('T')[0];
      const ga4PreviousEndDate = previousWeekEndDate.toISOString().split('T')[0];

      const [
        sessionStats,
        retention,
        weekly,
        ga4Analytics,
        previousWeekRetention,
        previousWeekGA4Analytics,
        currentAbandonmentRate,
        previousAbandonmentRate,
        currentPlatformData,
        previousPlatformData
      ] = await Promise.all([
        getDailySessionStats(startDate, endDate),
        getRetentionStats(),
        getWeeklyStats(),
        getGA4AnalyticsDashboard(ga4StartDate, ga4EndDate),
        getRetentionStats(), // This will get the same data, we'll need to modify the service
        getGA4AnalyticsDashboard(ga4PreviousStartDate, ga4PreviousEndDate),
        calculateAbandonmentRate(startDate, endDate),
        calculateAbandonmentRate(previousWeekStartDate, previousWeekEndDate),
        fetchPlatformData(startDate, endDate),
        fetchPlatformData(previousWeekStartDate, previousWeekEndDate)
      ]);

      console.log('ga4Analytics', ga4Analytics)
      console.log('previousWeekGA4Analytics', previousWeekGA4Analytics)

      setDailySessionStats(sessionStats);
      setRetentionStats(retention);
      setWeeklyStats(weekly);
      setGA4Data(ga4Analytics);
      setPreviousWeekRetentionStats(previousWeekRetention);
      setPreviousWeekGA4Data(previousWeekGA4Analytics);
      setAbandonmentRate(currentAbandonmentRate);
      setPreviousWeekAbandonmentRate(previousAbandonmentRate);
      setPlatformData(currentPlatformData);
      setPreviousWeekPlatformData(previousPlatformData);

      console.log('retentionStats:', retention);
      console.log('sessionStats:', sessionStats);
    } catch (error) {
      console.error('Session analytics fetch error:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSessionAnalytics();
  }, [selectionRange]);

  const handleSelect = (ranges) => {
    if (ranges.selection) {
      setSelectionRange(ranges.selection);
    }
  };

  const formatTime = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);
    
    if (hours > 0) {
      return `${hours}sa ${minutes}dk`;
    } else if (minutes > 0) {
      return `${minutes}dk ${secs}sn`;
    } else {
      return `${secs}sn`;
    }
  };

  const formatPercentage = (value) => {
    return `${value.toFixed(1)}%`;
  };

  // Fetch platform data using specific GA4 API call
  const fetchPlatformData = async (startDate, endDate) => {
    try {
      const platformRequest = {
        dateRanges: [
          {
            startDate: startDate.toISOString().split('T')[0],
            endDate: endDate.toISOString().split('T')[0]
          }
        ],
        dimensions: [
          { name: "platform" }
        ],
        metrics: [
          { name: "newUsers" },
          { name: "sessions" }
        ]
      };

      console.log('Platform Request:', platformRequest);

      // Mock data - replace with actual API call
      // This would be the response from GA4 API
      const mockResponse = {
        rows: [
          {
            dimensionValues: [{ value: "iOS" }],
            metricValues: [
              { value: "150" }, // newUsers
              { value: "1200" }  // sessions
            ]
          },
          {
            dimensionValues: [{ value: "Android" }],
            metricValues: [
              { value: "200" }, // newUsers
              { value: "1800" }  // sessions
            ]
          },
          {
            dimensionValues: [{ value: "Web" }],
            metricValues: [
              { value: "50" },  // newUsers
              { value: "300" }   // sessions
            ]
          }
        ]
      };

      // Process the response
      const platforms = mockResponse.rows.map(row => ({
        platform: row.dimensionValues[0].value,
        newUsers: parseInt(row.metricValues[0].value),
        sessions: parseInt(row.metricValues[1].value)
      }));

      return platforms;
    } catch (error) {
      console.error('Error fetching platform data:', error);
      return null;
    }
  };

  // Calculate abandonment rate using specific GA4 API calls
  const calculateAbandonmentRate = async (startDate, endDate) => {
    try {
      // First request: Get new users (first_open event)
      const firstOpenRequest = {
        dateRanges: [
          {
            startDate: startDate.toISOString().split('T')[0],
            endDate: endDate.toISOString().split('T')[0]
          }
        ],
        dimensions: [
          { name: "userPseudoId" }
        ],
        metrics: [
          { name: "activeUsers" }
        ],
        dimensionFilter: {
          filter: {
            fieldName: "eventName",
            stringFilter: {
              value: "first_open"
            }
          }
        }
      };

      // Second request: Get returning users (session_start event in next 2 weeks)
      const sessionStartEndDate = new Date(endDate);
      sessionStartEndDate.setDate(sessionStartEndDate.getDate() + 14); // Add 2 weeks

      const sessionStartRequest = {
        dateRanges: [
          {
            startDate: endDate.toISOString().split('T')[0],
            endDate: sessionStartEndDate.toISOString().split('T')[0]
          }
        ],
        dimensions: [
          { name: "userPseudoId" }
        ],
        dimensionFilter: {
          filter: {
            fieldName: "eventName",
            stringFilter: {
              value: "session_start"
            }
          }
        }
      };

      // Make API calls (you'll need to implement these with your GA4 service)
      // For now, we'll use mock data structure
      console.log('First Open Request:', firstOpenRequest);
      console.log('Session Start Request:', sessionStartRequest);

      // Mock calculation - replace with actual API calls
      const newUsers = 100; // This would come from first API call
      const returningUsers = 73; // This would come from second API call
      
      const abandonmentRate = 1 - (returningUsers / newUsers);
      
      return {
        newUsers,
        returningUsers,
        abandonmentRate: abandonmentRate * 100
      };
    } catch (error) {
      console.error('Error calculating abandonment rate:', error);
      return null;
    }
  };

  const dailyActiveUsersChart = () => {
    if (!weeklyStats?.dailyActiveCounts) return null;

    const data = {
      labels: weeklyStats.dailyActiveCounts.map(day => 
        new Date(day.date).toLocaleDateString('tr-TR')
      ),
      datasets: [{
        label: 'Günlük Aktif Kullanıcı',
        data: weeklyStats.dailyActiveCounts.map(day => day.count),
        borderColor: 'rgba(16, 185, 129, 1)',
        backgroundColor: 'rgba(16, 185, 129, 0.1)',
        borderWidth: 3,
        tension: 0.4,
        fill: true,
        pointBackgroundColor: 'rgba(16, 185, 129, 1)',
        pointBorderColor: 'white',
        pointBorderWidth: 2,
        pointRadius: 6,
        pointHoverRadius: 8,
      }],
    };

    const options = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: true,
          position: 'top',
          labels: {
            usePointStyle: true,
            padding: 20,
            font: {
              size: 12,
              weight: 'bold'
            }
          }
        },
        tooltip: {
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          titleColor: 'white',
          bodyColor: 'white',
          borderColor: 'rgba(16, 185, 129, 1)',
          borderWidth: 1,
          cornerRadius: 8,
          displayColors: false,
          callbacks: {
            label: function(context) {
              return `${context.parsed.y} aktif kullanıcı`;
            }
          }
        }
      },
      scales: {
        x: {
          grid: {
            display: false
          },
          ticks: {
            font: {
              size: 11
            },
            maxRotation: 45
          }
        },
        y: {
          beginAtZero: true,
          grid: {
            color: 'rgba(0, 0, 0, 0.05)'
          },
          ticks: {
            font: {
              size: 11
            }
          }
        }
      }
    };

    return (
      <div className="h-80 w-full">
        <Line data={data} options={options} />
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="flex flex-col items-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
          <div className="text-lg font-medium text-gray-600">Yükleniyor...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center mb-8 gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Oturum İstatistikleri</h1>
          <p className="text-gray-600">Detaylı oturum analitikleri ve kullanıcı davranış raporları</p>
        </div>
        <div className="flex flex-col lg:flex-row gap-4">
          <div className="w-full lg:w-auto">
            <div className="bg-white rounded-lg shadow-sm border p-2 overflow-x-auto">
              <DateRangePicker
                ranges={ranges}
                onChange={handleSelect}
                locale={tr}
                showSelectionPreview={true}
                moveRangeOnFirstSelection={false}
                months={1}
                direction="horizontal"
                className="w-full"
                style={{ minWidth: '600px' }}
              />
            </div>
          </div>
        </div>
      </div>
      {/* Kullanıcı Terk Etme Oranı */}
      {/* <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-6 mb-8">
        <div className="flex items-center mb-6">
          <div className="w-1 h-8 bg-red-500 rounded-full mr-3"></div>
          <h2 className="text-2xl font-bold text-gray-800">Kullanıcı Terk Etme Oranı (Son 2 Hafta)</h2>
        </div>
        {retentionStats && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-gradient-to-br from-red-50 to-red-100 p-6 rounded-xl border border-red-200">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-semibold text-red-800 text-sm uppercase tracking-wide">Terk Etme Oranı</h3>
                  <p className="text-3xl font-bold text-red-900 mt-2">{formatPercentage(retentionStats.churnRate)}</p>
                </div>
                <div className="w-12 h-12 bg-red-500 rounded-lg flex items-center justify-center">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                </div>
              </div>
            </div>
            <div className="bg-gradient-to-br from-emerald-50 to-emerald-100 p-6 rounded-xl border border-emerald-200">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-semibold text-emerald-800 text-sm uppercase tracking-wide">Kalan Kullanıcı</h3>
                  <p className="text-3xl font-bold text-emerald-900 mt-2">{retentionStats.retainedUsers}</p>
                </div>
                <div className="w-12 h-12 bg-emerald-500 rounded-lg flex items-center justify-center">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
              </div>
            </div>
            <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-6 rounded-xl border border-blue-200">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-semibold text-blue-800 text-sm uppercase tracking-wide">Yeni Kullanıcı</h3>
                  <p className="text-3xl font-bold text-blue-900 mt-2">{retentionStats.newUsers}</p>
                </div>
                <div className="w-12 h-12 bg-blue-500 rounded-lg flex items-center justify-center">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                  </svg>
                </div>
              </div>
            </div>
          </div>
        )}
      </div> */}

      {/* Haftalık Karşılaştırma */}
      <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-6 mb-8">
        <div className="flex items-center mb-6">
          <div className="w-1 h-8 bg-purple-500 rounded-full mr-3"></div>
          <h2 className="text-2xl font-bold text-gray-800">Haftalık Karşılaştırma</h2>
        </div>
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Yeni Kullanıcı Karşılaştırması */}
          <div className="bg-gradient-to-br from-purple-50 to-purple-100 p-6 rounded-xl border border-purple-200">
            <h3 className="text-lg font-semibold text-purple-800 mb-4">Yeni Kullanıcı Sayısı Değişimi</h3>
            
            {(() => {
              // Get current week data
              let currentNewUsers = 0;
              let previousNewUsers = 0;
              
              // Try GA4 data first
              if (ga4Data && ga4Data.success && previousWeekGA4Data && previousWeekGA4Data.success) {
                const currentFormatted = formatGA4DataForDashboard(ga4Data);
                const previousFormatted = formatGA4DataForDashboard(previousWeekGA4Data);
                currentNewUsers = currentFormatted?.totalNewUsers || 0;
                previousNewUsers = previousFormatted?.totalNewUsers || 0;
              }
              // Fallback to retention stats
              else if (retentionStats && previousWeekRetentionStats) {
                currentNewUsers = retentionStats.newUsers || 0;
                previousNewUsers = previousWeekRetentionStats.newUsers || 0;
              }
              
              const change = currentNewUsers - previousNewUsers;
              const changePercentage = previousNewUsers > 0 ? (change / previousNewUsers) * 100 : 0;
              const isIncrease = change > 0;
              const isDecrease = change < 0;
              
              return (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-purple-600">Bu Hafta</p>
                      <p className="text-2xl font-bold text-purple-900">{currentNewUsers}</p>
                    </div>
                    <div>
                      <p className="text-sm text-purple-600">Geçen Hafta</p>
                      <p className="text-2xl font-bold text-purple-900">{previousNewUsers}</p>
                    </div>
                  </div>
                  
                  <div className="text-center">
                    <div className={`inline-flex items-center px-4 py-2 rounded-full ${
                      isIncrease ? 'bg-green-100 text-green-800' : 
                      isDecrease ? 'bg-red-100 text-red-800' : 
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {isIncrease && <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 17l9.2-9.2M17 17V7H7" />
                      </svg>}
                      {isDecrease && <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 7l-9.2 9.2M7 7v10h10" />
                      </svg>}
                      <span className="font-bold">
                        {change > 0 ? '+' : ''}{change} kullanıcı
                      </span>
                    </div>
                    <p className="text-sm text-purple-600 mt-2">
                      {changePercentage > 0 ? '+' : ''}{changePercentage.toFixed(1)}% değişim
                    </p>
                  </div>
                </div>
              );
            })()}
          </div>

          {/* Aktif Kullanıcı Karşılaştırması */}
          <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-6 rounded-xl border border-blue-200">
            <h3 className="text-lg font-semibold text-blue-800 mb-4">Aktif Kullanıcı Sayısı Değişimi</h3>
            
            {(() => {
              // Get current week data
              let currentActiveUsers = 0;
              let previousActiveUsers = 0;
              
              // Try GA4 data first
              if (ga4Data && ga4Data.success && previousWeekGA4Data && previousWeekGA4Data.success) {
                const currentFormatted = formatGA4DataForDashboard(ga4Data);
                const previousFormatted = formatGA4DataForDashboard(previousWeekGA4Data);
                currentActiveUsers = currentFormatted?.totalActiveUsers || 0;
                previousActiveUsers = previousFormatted?.totalActiveUsers || 0;
              }
              // Fallback to retention stats
              else if (retentionStats && previousWeekRetentionStats) {
                currentActiveUsers = retentionStats.retainedUsers || 0;
                previousActiveUsers = previousWeekRetentionStats.retainedUsers || 0;
              }
              
              const change = currentActiveUsers - previousActiveUsers;
              const changePercentage = previousActiveUsers > 0 ? (change / previousActiveUsers) * 100 : 0;
              const isIncrease = change > 0;
              const isDecrease = change < 0;
              
              return (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-blue-600">Bu Hafta</p>
                      <p className="text-2xl font-bold text-blue-900">{currentActiveUsers}</p>
                    </div>
                    <div>
                      <p className="text-sm text-blue-600">Geçen Hafta</p>
                      <p className="text-2xl font-bold text-blue-900">{previousActiveUsers}</p>
                    </div>
                  </div>
                  
                  <div className="text-center">
                    <div className={`inline-flex items-center px-4 py-2 rounded-full ${
                      isIncrease ? 'bg-green-100 text-green-800' : 
                      isDecrease ? 'bg-red-100 text-red-800' : 
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {isIncrease && <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 17l9.2-9.2M17 17V7H7" />
                      </svg>}
                      {isDecrease && <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 7l-9.2 9.2M7 7v10h10" />
                      </svg>}
                      <span className="font-bold">
                        {change > 0 ? '+' : ''}{change} kullanıcı
                      </span>
                    </div>
                    <p className="text-sm text-blue-600 mt-2">
                      {changePercentage > 0 ? '+' : ''}{changePercentage.toFixed(1)}% değişim
                    </p>
                  </div>
                </div>
              );
            })()}
          </div>
        </div>

      </div>

      {/* Haftalık İstatistikler */}
      {/* <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-6 mb-8">
        <div className="flex items-center mb-6">
          <div className="w-1 h-8 bg-emerald-500 rounded-full mr-3"></div>
          <h2 className="text-2xl font-bold text-gray-800">Haftalık İstatistikler</h2>
        </div>
        {weeklyStats && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <div className="bg-gradient-to-br from-teal-50 to-teal-100 p-6 rounded-xl border border-teal-200">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-semibold text-teal-800 text-sm uppercase tracking-wide">Haftalık Aktif Kullanıcı</h3>
                  <p className="text-3xl font-bold text-teal-900 mt-2">{weeklyStats.weeklyActiveUsers}</p>
                </div>
                <div className="w-12 h-12 bg-teal-500 rounded-lg flex items-center justify-center">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                </div>
              </div>
            </div>
            <div className="bg-gradient-to-br from-cyan-50 to-cyan-100 p-6 rounded-xl border border-cyan-200">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-semibold text-cyan-800 text-sm uppercase tracking-wide">Aktif Cihaz</h3>
                  <p className="text-3xl font-bold text-cyan-900 mt-2">{weeklyStats.activeDevices}</p>
                </div>
                <div className="w-12 h-12 bg-cyan-500 rounded-lg flex items-center justify-center">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
                  </svg>
                </div>
              </div>
            </div>
            <div className="bg-gradient-to-br from-emerald-50 to-emerald-100 p-6 rounded-xl border border-emerald-200">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-semibold text-emerald-800 text-sm uppercase tracking-wide">Ortalama Günlük Aktif</h3>
                  <p className="text-3xl font-bold text-emerald-900 mt-2">{weeklyStats.averageDAU.toFixed(0)}</p>
                </div>
                <div className="w-12 h-12 bg-emerald-500 rounded-lg flex items-center justify-center">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                </div>
              </div>
            </div>
          </div>
        )}
        <div className="bg-gray-50 rounded-lg p-4">
          {dailyActiveUsersChart()}
        </div>
      </div> */}

      {/* Google Analytics 4 Data */}
      {ga4Data && ga4Data.success && (
        <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-6 mb-8">
          <div className="flex items-center mb-6">
            <div className="w-1 h-8 bg-blue-500 rounded-full mr-3"></div>
            <h2 className="text-2xl font-bold text-gray-800">Uygulama İstatistikleri</h2>
          </div>
          
          {(() => {
            const formattedData = formatGA4DataForDashboard(ga4Data);
            if (!formattedData) return <div>GA4 verisi yükleniyor...</div>;
            
            return (
              <>
                {/* GA4 Basic Stats */}
                <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-2 gap-6 mb-8">
                  <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-6 rounded-xl border border-blue-200">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="font-semibold text-blue-800 text-sm uppercase tracking-wide">Aktif Kullanıcı</h3>
                        <p className="text-3xl font-bold text-blue-900 mt-2">{formattedData.totalActiveUsers}</p>
                      </div>
                      <div className="w-12 h-12 bg-blue-500 rounded-lg flex items-center justify-center">
                        <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                        </svg>
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-gradient-to-br from-green-50 to-green-100 p-6 rounded-xl border border-green-200">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="font-semibold text-green-800 text-sm uppercase tracking-wide">Yeni Kullanıcı</h3>
                        <p className="text-3xl font-bold text-green-900 mt-2">{formattedData.totalNewUsers}</p>
                      </div>
                      <div className="w-12 h-12 bg-green-500 rounded-lg flex items-center justify-center">
                        <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                        </svg>
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-gradient-to-br from-purple-50 to-purple-100 p-6 rounded-xl border border-purple-200">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="font-semibold text-purple-800 text-sm uppercase tracking-wide">Toplam Oturum</h3>
                        <p className="text-3xl font-bold text-purple-900 mt-2">{formattedData.totalSessions}</p>
                      </div>
                      <div className="w-12 h-12 bg-purple-500 rounded-lg flex items-center justify-center">
                        <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                        </svg>
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-gradient-to-br from-orange-50 to-orange-100 p-6 rounded-xl border border-orange-200">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="font-semibold text-orange-800 text-sm uppercase tracking-wide">Bir Çocuğun Oyuna Girme Sayısı</h3>
                        <p className="text-3xl font-bold text-orange-900 mt-2">{parseFloat(formattedData.totalSessions/formattedData.totalActiveUsers).toFixed(2)}</p>
                      </div>
                      <div className="w-12 h-12 bg-orange-500 rounded-lg flex items-center justify-center">
                        <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                      </div>
                    </div>
                  </div>
                </div>

                {/* GA4 Daily Users Chart */}
                {formattedData.dailyActiveUsers && formattedData.dailyActiveUsers.length > 0 && (
                  <div className="mb-8">
                    <h3 className="text-lg font-semibold text-gray-800 mb-4">Günlük Aktif Kullanıcılar</h3>
                    <div className="bg-gray-50 rounded-lg p-4">
                      <div className="h-80 w-full">
                        <Line 
                          data={{
                            labels: formattedData.dailyActiveUsers.map(day => day.date),
                            datasets: [{
                              label: 'Aktif Kullanıcı',
                              data: formattedData.dailyActiveUsers.map(day => day.count),
                              borderColor: 'rgba(59, 130, 246, 1)',
                              backgroundColor: 'rgba(59, 130, 246, 0.1)',
                              borderWidth: 3,
                              tension: 0.4,
                              fill: true,
                              pointBackgroundColor: 'rgba(59, 130, 246, 1)',
                              pointBorderColor: 'white',
                              pointBorderWidth: 2,
                              pointRadius: 6,
                              pointHoverRadius: 8,
                            }],
                          }}
                          options={{
                            responsive: true,
                            maintainAspectRatio: false,
                            plugins: {
                              legend: {
                                display: true,
                                position: 'top',
                                labels: {
                                  usePointStyle: true,
                                  padding: 20,
                                  font: {
                                    size: 12,
                                    weight: 'bold'
                                  }
                                }
                              },
                              tooltip: {
                                backgroundColor: 'rgba(0, 0, 0, 0.8)',
                                titleColor: 'white',
                                bodyColor: 'white',
                                borderColor: 'rgba(59, 130, 246, 1)',
                                borderWidth: 1,
                                cornerRadius: 8,
                                displayColors: false,
                                callbacks: {
                                  label: function(context) {
                                    return `${context.parsed.y} aktif kullanıcı`;
                                  }
                                }
                              }
                            },
                            scales: {
                              x: {
                                grid: {
                                  display: false
                                },
                                ticks: {
                                  font: {
                                    size: 11
                                  },
                                  maxRotation: 45
                                }
                              },
                              y: {
                                beginAtZero: true,
                                grid: {
                                  color: 'rgba(0, 0, 0, 0.05)'
                                },
                                ticks: {
                                  font: {
                                    size: 11
                                  }
                                }
                              }
                            }
                          }}
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* GA4 Device Stats */}
                {/* {formattedData.deviceStats && formattedData.deviceStats.length > 0 && (
                  <div className="mb-8">
                    <h3 className="text-lg font-semibold text-gray-800 mb-4">GA4 Cihaz İstatistikleri</h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      {formattedData.deviceStats.map((device, index) => (
                        <div key={device.device} className="bg-gray-50 rounded-lg p-4">
                          <h4 className="font-semibold text-gray-800 mb-2">{device.device}</h4>
                          <div className="space-y-2">
                            <div className="flex justify-between">
                              <span className="text-sm text-gray-600">Kullanıcı:</span>
                              <span className="font-bold text-blue-600">{device.users}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-sm text-gray-600">Oturum:</span>
                              <span className="font-bold text-green-600">{device.sessions}</span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )} */}
              </>
            );
          })()}
        </div>
      )}

      {/* Yeni Kullanıcı Terk Etme Oranı */}
      {/* <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-6 mb-8">
        <div className="flex items-center mb-6">
          <div className="w-1 h-8 bg-red-500 rounded-full mr-3"></div>
          <h2 className="text-2xl font-bold text-gray-800">Yeni Kullanıcı Terk Etme Analizi</h2>
        </div>
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div className="bg-gradient-to-br from-red-50 to-red-100 p-8 rounded-xl border border-red-200">
            <div className="text-center">
              <div className="w-20 h-20 bg-red-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-red-800 mb-2">Uygulamayı Terk Eden Yeni Kullanıcılar</h3>
              <p className="text-4xl font-bold text-red-900 mb-2">
                {(() => {
                  // Try retention stats first
                  if (retentionStats && retentionStats.newUsers > 0) {
                    return formatPercentage((retentionStats.newUsers - retentionStats.retainedUsers) / retentionStats.newUsers * 100);
                  }
                  
                  // Fallback to GA4 data if available
                  if (ga4Data && ga4Data.success) {
                    const formattedData = formatGA4DataForDashboard(ga4Data);
                    if (formattedData && formattedData.totalNewUsers > 0 && formattedData.totalActiveUsers > 0) {
                      const abandonmentRate = ((formattedData.totalNewUsers - formattedData.totalActiveUsers) / formattedData.totalNewUsers) * 100;
                      return formatPercentage(Math.max(0, abandonmentRate));
                    }
                  }
                  
                  return '0%';
                })()}
              </p>
              <p className="text-sm text-red-700">
                {retentionStats && retentionStats.newUsers > 0 
                  ? `${retentionStats.newUsers - retentionStats.retainedUsers} / ${retentionStats.newUsers} kullanıcı`
                  : 'Veri yok'
                }
              </p>
              <p className="text-xs text-gray-500 mt-2">
                Debug: retentionStats = {JSON.stringify(retentionStats)}
              </p>
              <p className="text-xs text-gray-500 mt-1">
                GA4 Data: {ga4Data && ga4Data.success ? 'Available' : 'Not available'}
              </p>
            </div>
          </div>

          <div className="space-y-6">
            <div className="bg-gray-50 rounded-lg p-6">
              <h4 className="text-lg font-semibold text-gray-800 mb-4">Analiz Detayları</h4>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Toplam Yeni Kullanıcı:</span>
                  <span className="font-bold text-blue-600">
                    {(() => {
                      if (retentionStats && retentionStats.newUsers > 0) {
                        return retentionStats.newUsers;
                      }
                      if (ga4Data && ga4Data.success) {
                        const formattedData = formatGA4DataForDashboard(ga4Data);
                        return formattedData?.totalNewUsers || 0;
                      }
                      return 0;
                    })()}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Kalan Kullanıcı:</span>
                  <span className="font-bold text-green-600">
                    {(() => {
                      if (retentionStats && retentionStats.retainedUsers > 0) {
                        return retentionStats.retainedUsers;
                      }
                      if (ga4Data && ga4Data.success) {
                        const formattedData = formatGA4DataForDashboard(ga4Data);
                        return formattedData?.totalActiveUsers || 0;
                      }
                      return 0;
                    })()}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Terk Eden Kullanıcı:</span>
                  <span className="font-bold text-red-600">
                    {(() => {
                      if (retentionStats && retentionStats.newUsers > 0) {
                        return retentionStats.newUsers - retentionStats.retainedUsers;
                      }
                      if (ga4Data && ga4Data.success) {
                        const formattedData = formatGA4DataForDashboard(ga4Data);
                        if (formattedData?.totalNewUsers && formattedData?.totalActiveUsers) {
                          return Math.max(0, formattedData.totalNewUsers - formattedData.totalActiveUsers);
                        }
                      }
                      return 0;
                    })()}
                  </span>
                </div>
              </div>
            </div>

            <div className="bg-blue-50 rounded-lg p-6">
              <h4 className="text-lg font-semibold text-blue-800 mb-2">Açıklama</h4>
              <p className="text-sm text-blue-700">
                Bu metrik, son 1-2 hafta içinde uygulamayı ilk kez yükleyen kullanıcıların 
                kaçının uygulamayı terk ettiğini gösterir. Yüksek oranlar, kullanıcı deneyimi 
                veya onboarding sürecinde iyileştirme alanları olduğunu işaret edebilir.
              </p>
            </div>
          </div>
        </div>

        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-6 rounded-xl border border-blue-200">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-semibold text-blue-800 text-sm uppercase tracking-wide">Terk Etme Oranı</h3>
                <p className="text-2xl font-bold text-blue-900 mt-2">
                  {retentionStats && retentionStats.newUsers > 0 
                    ? formatPercentage((retentionStats.newUsers - retentionStats.retainedUsers) / retentionStats.newUsers * 100)
                    : '0%'
                  }
                </p>
              </div>
              <div className="w-12 h-12 bg-blue-500 rounded-lg flex items-center justify-center">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-green-50 to-green-100 p-6 rounded-xl border border-green-200">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-semibold text-green-800 text-sm uppercase tracking-wide">Kalan Kullanıcı Oranı</h3>
                <p className="text-2xl font-bold text-green-900 mt-2">
                  {retentionStats && retentionStats.newUsers > 0 
                    ? formatPercentage(retentionStats.retainedUsers / retentionStats.newUsers * 100)
                    : '0%'
                  }
                </p>
              </div>
              <div className="w-12 h-12 bg-green-500 rounded-lg flex items-center justify-center">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-purple-50 to-purple-100 p-6 rounded-xl border border-purple-200">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-semibold text-purple-800 text-sm uppercase tracking-wide">Analiz Dönemi</h3>
                <p className="text-lg font-bold text-purple-900 mt-2">1-2 Hafta</p>
              </div>
              <div className="w-12 h-12 bg-purple-500 rounded-lg flex items-center justify-center">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>
          </div>
        </div>
      </div> */}

      {/* Cihaz İstatistikleri */}
      <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-6 mb-8">
        <div className="flex items-center mb-6">
          <div className="w-1 h-8 bg-indigo-500 rounded-full mr-3"></div>
          <h2 className="text-2xl font-bold text-gray-800">Cihaz İstatistikleri</h2>
        </div>
        
        {platformData && platformData.length > 0 && (
          <div className="space-y-8">
            {/* Platform Overview */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* iOS Stats */}
              {platformData.find(p => p.platform === 'iOS') && (
                <div className="bg-gradient-to-br from-gray-50 to-gray-100 p-6 rounded-xl border border-gray-200">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center">
                      <div className="w-12 h-12 bg-gray-800 rounded-lg flex items-center justify-center mr-3">
                        <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                        </svg>
                      </div>
                      <h3 className="text-xl font-bold text-gray-800">iOS</h3>
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-bold text-gray-900">
                        {platformData.find(p => p.platform === 'iOS')?.newUsers || 0}
                      </p>
                      <p className="text-sm text-gray-600">yeni kullanıcı</p>
                    </div>
                  </div>
                  
                  <div className="bg-white rounded-lg p-3">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">Toplam Oturum</span>
                      <span className="font-bold text-blue-600">
                        {platformData.find(p => p.platform === 'iOS')?.sessions || 0}
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {/* Android Stats */}
              {platformData.find(p => p.platform === 'Android') && (
                <div className="bg-gradient-to-br from-green-50 to-green-100 p-6 rounded-xl border border-green-200">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center">
                      <div className="w-12 h-12 bg-green-600 rounded-lg flex items-center justify-center mr-3">
                        <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M17.523 15.3414c-.5511 0-.9993-.4486-.9993-.9997s.4482-.9993.9993-.9993c.5511 0 .9993.4482.9993.9993.0001.5511-.4482.9997-.9993.9997m-11.046 0c-.5511 0-.9993-.4486-.9993-.9997s.4482-.9993.9993-.9993c.5511 0 .9993.4482.9993.9993 0 .5511-.4482.9997-.9993.9997m11.4045-6.02l1.9973-3.4592a.416.416 0 00-.1521-.5676.416.416 0 00-.5676.1521l-2.0223 3.503C15.5902 8.2439 13.8533 7.8508 12 7.8508s-3.5902.3931-5.1367 1.1359L4.841 5.4834a.416.416 0 00-.5676-.1521.416.416 0 00-.1521.5676l1.9973 3.4592C2.6889 11.1867.3432 14.6589 0 18.761h24c-.3432-4.1021-2.6889-7.5743-6.1185-8.4396"/>
                        </svg>
                      </div>
                      <h3 className="text-xl font-bold text-gray-800">Android</h3>
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-bold text-gray-900">
                        {platformData.find(p => p.platform === 'Android')?.newUsers || 0}
                      </p>
                      <p className="text-sm text-gray-600">yeni kullanıcı</p>
                    </div>
                  </div>
                  
                  <div className="bg-white rounded-lg p-3">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">Toplam Oturum</span>
                      <span className="font-bold text-green-600">
                        {platformData.find(p => p.platform === 'Android')?.sessions || 0}
                      </span>
                    </div>
                  </div>
                </div>
              )}

      
            </div>

          </div>
        )}
        
        {(!platformData || platformData.length === 0) && (
          <div className="text-center py-8">
            <div className="text-gray-500">
              <svg className="w-12 h-12 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
              </svg>
              <p>Cihaz verisi yükleniyor...</p>
            </div>
          </div>
        )}
      </div>

    </div>
  );
}

export default SessionStats;
